/** Moves the item at `from` to position `to`, returning a new array. */
export function reorderList<T>(items: readonly T[], from: number, to: number): T[] {
  if (from === to || from < 0 || from >= items.length || to < 0 || to >= items.length) return [...items];
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/** Which row a pointer at vertical position `y` is over. Inside a row's box that row wins; in the
 * gaps between rows (or past the ends) the nearest row by centre does, so a finger that drifts a
 * few pixels off a row still points at something sensible. Rows are given as {top, bottom}. */
export function rowIndexAtY(rows: readonly { top: number; bottom: number }[], y: number): number {
  let best = 0;
  let bestDistance = Infinity;
  rows.forEach((r, i) => {
    const distance = y >= r.top && y <= r.bottom ? 0 : Math.abs(y - (r.top + r.bottom) / 2);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = i;
    }
  });
  return best;
}

const sameFile = (a: { name: string; size: number }, b: { name: string; size: number }) => a.name === b.name && a.size === b.size;

/** Which of `incoming` are already present -- in `existing`, OR earlier in the same selection.
 * (QA retest 6: the same file twice inside one selection produced no warning at all.) */
export function findDuplicateFiles<T extends { name: string; size: number }>(existing: readonly T[], incoming: readonly T[]): T[] {
  const seen: T[] = [...existing];
  const dupes: T[] = [];
  for (const f of incoming) {
    if (seen.some((e) => sameFile(e, f))) dupes.push(f);
    seen.push(f);
  }
  return dupes;
}

/** Indexes of list items that repeat an EARLIER item (the first copy isn't flagged), for a row tag. */
export function duplicateIndexes<T extends { name: string; size: number }>(files: readonly T[]): Set<number> {
  const out = new Set<number>();
  files.forEach((f, i) => {
    if (files.slice(0, i).some((e) => sameFile(e, f))) out.add(i);
  });
  return out;
}
